import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Container,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Box } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { ExcelRenderer } from 'react-excel-renderer';
import { BiCloudDownload, BiCloudUpload } from 'react-icons/bi';
import { pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import { fDate } from '../../utils/formatTime';
import Loader from '../Global/Loader';
import { DateRangeStyled } from '../Global/Styling';
import RevenueService from '../services/RevenueService';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getMasterListType,
  getStateData,
  getCityData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const { UploadMisFile, UploadRevenueFile, DownloadMisTemplate } = RevenueService;

const TABLE_HEAD = [
  { id: 'bankName', label: 'BANK NAME', alignRight: false },
  { id: 'fileNo', label: 'FILE NO', alignRight: false },
  { id: 'branchName', label: 'BRANCH NAME', alignRight: false },
  { id: 'disbDate', label: 'DISB DATE', alignRight: false },
  { id: 'borrowerName', label: 'BORROWER NAME', alignRight: false },
  { id: 'empClass', label: 'EMP CLASS', alignRight: false },
  { id: 'loanProductType', label: 'LOAN PRODUCT TYPE', alignRight: false },
  { id: 'disbAmount', label: 'DISB AMOUNT', alignRight: false }
];

function UploadBulkFile() {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadfile, setUploadfile] = useState(null);
  const [openLoader, setOpenLoader] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);

  const [fileDetails, setFileDetails] = useState({
    Source: '',
    SourceDate: '',
    FileType: '', // will store MasterTypeListDrop.value (128 / 130 / 132)
    StateId: null,
    DistrictId: null,
    period: {
      startDate: null,
      endDate: null
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { MasterTypeListDrop, stateDrop, CityDrop } = useGlobalSelector();

  // Master Type List
  const getMasterTypeListApi = useCallback(() => {
    const type = 'REVENUE_FILE_STATUS_TYPE';
    dispatch(getMasterListType(type));
  }, [dispatch]);

  // State → District cascading
  const handleStateChange = (newStateOption) => {
    dispatch(setCityData([]));
    setFileDetails((prev) => ({ ...prev, StateId: newStateOption, DistrictId: null }));
    if (newStateOption?.value) dispatch(getCityData({ stateId: newStateOption.value }));
  };

  const handleRenderFile = (fileObj) => {
    ExcelRenderer(fileObj, (err, resp) => {
      if (err) {
        console.error('Error reading excel file', err);
        setOpenLoader(false);
        return;
      }
      // If you need to process `resp.rows`, do it here…
      setOpenLoader(false);
    });
  };

  // SIMPLE, SAFE FILE HANDLER
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();

    setSelectedFileName(fileName);
    setUploadfile(file);

    if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
      setOpenLoader(true);
      handleRenderFile(file);
    } else {
      // reset file if wrong type
      setUploadfile(null);
      setSelectedFileName('');
      dispatch(setApiError('Please add only xlsx/csv/xls file only'));
    }
  };
  // console.log('sss uploadfile:', uploadfile);
  // console.log('sss uploadfile size:', uploadfile?.size);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const revenueFileTypeId = fileDetails.FileType;

    if (!revenueFileTypeId) {
      dispatch(setApiError('Please select file type'));
      return;
    }
    if (!uploadfile) {
      dispatch(setApiError('Please upload MIS file'));
      return;
    }

    if (uploadfile && uploadfile.size > 0) {
      setOpenLoader(true);

      const formUploadData = new FormData();
      formUploadData.append('companyId', LoginData?.companyId);
      formUploadData.append('misFile', uploadfile);
      formUploadData.append(
        'periodFromDate',
        filterDatesRange[0].startDate ? formatDate(filterDatesRange[0].startDate) : ''
      );
      formUploadData.append(
        'periodToDate',
        filterDatesRange[0].endDate ? formatDate(filterDatesRange[0].endDate) : ''
      );
      formUploadData.append('revenueFileTypeId', revenueFileTypeId);
      formUploadData.append(
        'sourceDate',
        fileDetails.SourceDate !== '' ? fileDetails.SourceDate : '1970-01-01'
      );
      formUploadData.append('sourceName', fileDetails.Source !== '' ? fileDetails.Source : null);
      formUploadData.append('userId', LoginData?.userId);
      formUploadData.append('provinceId', fileDetails.StateId?.value || '');
      formUploadData.append('districtId', fileDetails.DistrictId?.value || '');

      fetch(UploadMisFile(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${LoginData?.jwt}`
        },
        body: formUploadData
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to upload file.');
          }
          return response.json();
        })
        .then((data) => {
          if (data.status === 'success') {
            setPreviewData(data?.data?.extractedData || []);
            dispatch(setApiSuccess('File uploaded successfully'));
          } else {
            console.error('Upload failed:', data.message);
            dispatch(setApiError(data.message || 'Upload failed'));
          }
        })
        .catch((error) => {
          console.error('Error uploading file:', error);
          dispatch(setApiError('Error uploading file'));
        })
        .finally(() => {
          setOpenLoader(false);
        });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const DownloadMisTemplateApiResponse = await DownloadMisTemplate(LoginData?.jwt);
      const blob = await DownloadMisTemplateApiResponse.blob();
      const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'revenue-mis-file-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(setApiSuccess('Template Successfully Downloaded'));
    } catch (error) {
      console.error('Error downloading template:', error);
      dispatch(setApiError('Error downloading template'));
    }
  };

  useEffect(() => {
    getMasterTypeListApi();
    dispatch(getStateData({ countryId: 101 }));

    // Optional: restore file type from localStorage if previously saved
    const stored = localStorage.getItem('Revenue');
    if (stored) {
      try {
        const authRevenue = JSON.parse(stored);
        setFileDetails((prev) => ({
          ...prev,
          FileType:
            typeof authRevenue === 'number' ? authRevenue : authRevenue?.value || prev.FileType
        }));
      } catch (err) {
        console.error('Error parsing Revenue from localStorage', err);
      }
    }
  }, [getMasterTypeListApi]);

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Upload Files" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>

          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <Grid container spacing={3} mb={3}>
              {/* Source */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Source
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{
                      className: 'textInput'
                    }}
                    placeholder="Type Source"
                    value={fileDetails.Source}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFileDetails((prevData) => ({
                        ...prevData,
                        Source: value
                      }));
                    }}
                  />
                </Box>
              </Grid>

              {/* Source Date */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Source Date
                  </Typography>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={fileDetails.SourceDate ? dayjs(fileDetails.SourceDate) : null}
                    onChange={(newValue) => {
                      const formattedValue =
                        newValue && dayjs(newValue).isValid()
                          ? dayjs(newValue).format('YYYY-MM-DD')
                          : '';
                      setFileDetails((prevData) => ({
                        ...prevData,
                        SourceDate: formattedValue
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          placeholder: 'DD/MM/YYYY',
                          className: `textInput dissabledMenu${fileDetails.SourceDate}`
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* File Type */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    File Type
                  </Typography>
                  <Select
                    fullWidth
                    inputProps={{
                      className: `textInput customSelectBox dissabledMenu${fileDetails.FileType}`
                    }}
                    value={fileDetails.FileType || ''}
                    onChange={(e) => {
                      const value = e.target.value; // 128 / 130 / 132
                      setFileDetails((prevData) => ({
                        ...prevData,
                        FileType: value
                      }));
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select File Type
                    </MenuItem>
                    {MasterTypeListDrop.length > 0 &&
                      MasterTypeListDrop.map((item) => (
                        <MenuItem value={item.value} key={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              </Grid>

              {/* Period */}
              <Grid item xs={4} sx={{ position: 'relative' }}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Period
                  </Typography>
                  <Box className="displayDateRange">
                    {filterDatesRange[0].startDate !== null
                      ? `${fDate(filterDatesRange[0].startDate)} to `
                      : ''}
                    {filterDatesRange[0].endDate !== null ? fDate(filterDatesRange[0].endDate) : ''}
                    {filterDatesRange[0].startDate === null &&
                    filterDatesRange[0].endDate === null ? (
                      <Typography className="dissabledMenu">Select Date Range</Typography>
                    ) : (
                      ''
                    )}
                  </Box>
                  <Select
                    fullWidth
                    inputProps={{
                      className: 'textInput'
                    }}
                    MenuProps={{
                      className: 'menuOpened'
                    }}
                    value=" "
                    className="datesSelect"
                  >
                    <DateRangeStyled
                      editableDateInputs={false}
                      onChange={(item) => setFilterDatesRange([item.selection])}
                      moveRangeOnFirstSelection={false}
                      ranges={filterDatesRange}
                      className="dateRangePicker"
                    />
                  </Select>
                </Box>
              </Grid>

              {/* State */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    State
                  </Typography>
                  <Autocomplete
                    disablePortal
                    id="combo-box-state"
                    options={stateDrop || []}
                    size="small"
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => <li {...props}>{option?.label}</li>}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth placeholder="Select State" />
                    )}
                    value={fileDetails.StateId || null}
                    onChange={(e, newValue) => handleStateChange(newValue)}
                  />
                </Box>
              </Grid>

              {/* District / City */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    District / City
                  </Typography>
                  <Autocomplete
                    disablePortal
                    id="combo-box-district"
                    options={CityDrop || []}
                    size="small"
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => <li {...props}>{option?.label}</li>}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth placeholder="Select District / City" />
                    )}
                    value={fileDetails.DistrictId || null}
                    onChange={(e, newValue) =>
                      setFileDetails((prev) => ({ ...prev, DistrictId: newValue }))
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            {/* File upload + template download */}
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs mb={3}>
                <Box variant="Container" className="uploadBox uploadBoxSec">
                  <input
                    accept=".xlsx, .xls, .csv"
                    className="fileInput"
                    id="contained-button-file"
                    multiple
                    type="file"
                    onChange={handleFileChange}
                  />

                  <label htmlFor="contained-button-file">
                    <Box>
                      {selectedFileName ? (
                        <Typography variant="body2">
                          {`Selected file name : ${selectedFileName}`}
                        </Typography>
                      ) : (
                        <>
                          <BiCloudUpload className="upladIcon" />
                          <Typography variant="h5">Upload</Typography>
                          <Typography variant="body2">
                            Supported file types: .xls, .xlsx, .csv
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
              </Grid>

              <Grid xs="auto" item mb={3} sx={{ flexGrow: '1' }}>
                <Button
                  className="mainBtn downloadBtn downloadBtnSec otherBtn"
                  variant="contained"
                  onClick={handleDownloadTemplate}
                >
                  <BiCloudDownload className="downloadIcon" />
                  <Typography variant="subtitle1">Download Template</Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Preview Button */}
          <Box textAlign="right" sx={{ mt: 3 }}>
            <Button variant="contained" className="mainBtn" onClick={handleSubmit}>
              Upload
            </Button>
          </Box>

          {/* Preview Table */}
          {previewData && (
            <Box mt={4}>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      {TABLE_HEAD.map((headCell) => (
                        <StyledTableCell
                          key={headCell.id}
                          align={headCell.alignRight ? 'right' : 'left'}
                        >
                          {headCell.label}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((item, index) => (
                      <StyledTableRow hover key={index}>
                        <StyledTableCell align="left">
                          {item.bankEntity?.bankName || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell align="left">{item.fileNumber || 'N/A'}</StyledTableCell>
                        <StyledTableCell align="left">
                          {item.branchEntity?.branchName || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {item.fileDisbursedDate ? fDate(item.fileDisbursedDate) : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell align="left">{item.borrowerName || 'N/A'}</StyledTableCell>
                        <StyledTableCell align="left">{item.empClass || 'N/A'}</StyledTableCell>
                        <StyledTableCell align="left">
                          {item.loanType?.loanTypeName || 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {item.loanAmountDisbursed
                            ? item.loanAmountDisbursed.toLocaleString('en-IN')
                            : 'N/A'}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                    {previewData.length === 0 && (
                      <TableRow>
                        <StyledTableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          No data extracted.
                        </StyledTableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box textAlign="right">
                <Button
                  variant="contained"
                  className="mainBtn"
                  onClick={() => navigate('/revenue')}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          )}

          {/* Loader */}
          <Loader openLoader={openLoader} />
        </Box>
      </Container>
    </Page>
  );
}

export default UploadBulkFile;

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('-');
}
